import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { products as productsApi } from '../services/api.js';
import './ProductManagement.css';

export function ProductManagement() {
  const navigate = useNavigate();
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit/Add state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Form fields (internal names – mapped to/from PascalCase API fields)
  const [form, setForm] = useState({
    name: '',
    description: '',
    appetite_definition: '',
    underwriting_guidelines: '',
    min_premium: '',
    max_premium: '',
    min_deductible: '',
    max_deductible: '',
    min_limit: '',
    max_limit: '',
    exception_rules: '',
    active: true,
  });

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await productsApi.list();
      const items = result.data || result;
      setProductList(Array.isArray(items) ? items : []);
    } catch (err) {
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSelect = (product) => {
    setSelectedProduct(product);
    setIsEditing(false);
    setIsAdding(false);
    setSaveError(null);
  };

  const handleEdit = () => {
    if (!selectedProduct) return;
    const guardrails = selectedProduct.PricingGuardrails || {};
    const appetite = selectedProduct.AppetiteDefinition;
    const exceptions = selectedProduct.ExceptionRules;
    setForm({
      name: selectedProduct.Name || '',
      description: selectedProduct.Description || '',
      appetite_definition:
        typeof appetite === 'object' && appetite != null
          ? JSON.stringify(appetite, null, 2)
          : String(appetite || ''),
      underwriting_guidelines: selectedProduct.UnderwritingGuidelines || '',
      min_premium: guardrails.MinPremium ?? guardrails.min_premium ?? '',
      max_premium: guardrails.MaxPremium ?? guardrails.max_premium ?? '',
      min_deductible: guardrails.MinDeductible ?? guardrails.min_deductible ?? '',
      max_deductible: guardrails.MaxDeductible ?? guardrails.max_deductible ?? '',
      min_limit: guardrails.MinLimit ?? guardrails.min_limit ?? '',
      max_limit: guardrails.MaxLimit ?? guardrails.max_limit ?? '',
      exception_rules:
        typeof exceptions === 'object' && exceptions != null
          ? JSON.stringify(exceptions, null, 2)
          : String(exceptions || ''),
      active: selectedProduct.Active !== false,
    });
    setIsEditing(true);
    setIsAdding(false);
    setSaveError(null);
  };

  const handleAdd = () => {
    setSelectedProduct(null);
    setForm({
      name: '',
      description: '',
      appetite_definition: '',
      underwriting_guidelines: '',
      min_premium: '',
      max_premium: '',
      min_deductible: '',
      max_deductible: '',
      min_limit: '',
      max_limit: '',
      exception_rules: '',
      active: true,
    });
    setIsAdding(true);
    setIsEditing(false);
    setSaveError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsAdding(false);
    setSaveError(null);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      // Try to parse JSON fields
      let appetiteObj = form.appetite_definition;
      try {
        if (typeof appetiteObj === 'string' && appetiteObj.trim().startsWith('{')) {
          appetiteObj = JSON.parse(appetiteObj);
        }
      } catch {
        /* keep as string */
      }

      let exceptionObj = form.exception_rules;
      try {
        if (typeof exceptionObj === 'string' && exceptionObj.trim().startsWith('{')) {
          exceptionObj = JSON.parse(exceptionObj);
        }
      } catch {
        /* keep as string */
      }

      const payload = {
        Name: form.name,
        Description: form.description,
        AppetiteDefinition: appetiteObj || null,
        UnderwritingGuidelines: form.underwriting_guidelines,
        PricingGuardrails: {
          MinPremium: form.min_premium ? Number(form.min_premium) : null,
          MaxPremium: form.max_premium ? Number(form.max_premium) : null,
          MinDeductible: form.min_deductible ? Number(form.min_deductible) : null,
          MaxDeductible: form.max_deductible ? Number(form.max_deductible) : null,
          MinLimit: form.min_limit ? Number(form.min_limit) : null,
          MaxLimit: form.max_limit ? Number(form.max_limit) : null,
        },
        ExceptionRules: exceptionObj || null,
        Active: form.active,
      };

      if (isAdding) {
        await productsApi.create(payload);
      } else if (isEditing && selectedProduct) {
        await productsApi.update(selectedProduct.ProductID, payload);
      }

      setIsEditing(false);
      setIsAdding(false);
      loadProducts();
    } catch (err) {
      setSaveError(err.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const showForm = isEditing || isAdding;
  const prod = selectedProduct;

  /** Format a label from camelCase/snake_case to readable text */
  const formatLabel = (key) => {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  /** Format a number as currency if it looks like money */
  const formatNumber = (val, key) => {
    const k = (key || '').toLowerCase();
    if (k.includes('revenue') || k.includes('premium') || k.includes('deductible') || k.includes('limit') || k.includes('rate')) {
      if (val >= 1000) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
      }
      return `$${val}`;
    }
    return new Intl.NumberFormat('en-US').format(val);
  };

  /** Render a value based on its type */
  const renderValue = (val, key) => {
    if (val == null) return <span className="pm__val-empty">—</span>;

    // Arrays render as tag chips
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="pm__val-empty">None</span>;
      return (
        <div className="pm__tag-list">
          {val.map((item, i) => (
            <span key={i} className="pm__tag">{String(item)}</span>
          ))}
        </div>
      );
    }

    // Nested objects render as a mini key-value grid
    if (typeof val === 'object') {
      const entries = Object.entries(val);
      if (entries.length === 0) return <span className="pm__val-empty">—</span>;
      return (
        <div className="pm__nested-obj">
          {entries.map(([k, v]) => (
            <div key={k} className="pm__nested-row">
              <span className="pm__nested-key">{formatLabel(k)}:</span>
              <span className="pm__nested-val">
                {typeof v === 'number' ? formatNumber(v, k) : String(v ?? '—')}
              </span>
            </div>
          ))}
        </div>
      );
    }

    // Booleans
    if (typeof val === 'boolean') {
      return <span className={`pm__bool ${val ? 'pm__bool--yes' : 'pm__bool--no'}`}>{val ? 'Yes' : 'No'}</span>;
    }

    // Numbers
    if (typeof val === 'number') {
      return <span>{formatNumber(val, key)}</span>;
    }

    return <span>{String(val)}</span>;
  };

  /** Render an object or string as a readable detail block */
  const renderObjectOrText = (value, emptyMsg) => {
    if (value == null) return <p className="pm__detail-text">{emptyMsg}</p>;
    if (typeof value === 'string') return <p className="pm__detail-text">{value || emptyMsg}</p>;
    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) return <p className="pm__detail-text">{emptyMsg}</p>;
      return (
        <dl className="pm__guardrails-grid">
          {entries.map(([key, val]) => (
            <div key={key} className="pm__guardrail-item">
              <dt>{formatLabel(key)}</dt>
              <dd>{renderValue(val, key)}</dd>
            </div>
          ))}
        </dl>
      );
    }
    return <p className="pm__detail-text">{String(value)}</p>;
  };

  return (
    <div className="pm">
      <div className="pm__header">
        <h1 className="pm__title">Product Management</h1>
        <button className="btn btn--primary" onClick={handleAdd}>
          + Add Product
        </button>
      </div>

      <div className="pm__layout">
        {/* Product list */}
        <div className="pm__list-panel card">
          <h3 className="pm__panel-title">Products</h3>
          {error && <div className="pm__error-inline">{error}</div>}
          {loading ? (
            <div className="pm__loading-inline">
              <div className="spinner" />
            </div>
          ) : productList.length > 0 ? (
            <ul className="pm__product-list">
              {productList.map((p) => (
                <li
                  key={p.ProductID}
                  className={`pm__product-item ${
                    prod && prod.ProductID === p.ProductID ? 'pm__product-item--selected' : ''
                  }`}
                  onClick={() => navigate(`/products/${p.ProductID}`)}
                >
                  <span className="pm__product-name">{p.Name || '—'}</span>
                  <span
                    className={`pm__product-status ${p.Active ? 'pm__product-status--active' : ''}`}
                  >
                    {p.Active ? 'Active' : 'Inactive'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pm__empty">No products defined yet.</p>
          )}
        </div>

        {/* Detail / Form panel */}
        <div className="pm__detail-panel card">
          {showForm ? (
            <div className="pm__form">
              <h3 className="pm__panel-title">{isAdding ? 'Add Product' : 'Edit Product'}</h3>

              {saveError && <div className="pm__error-inline">{saveError}</div>}

              <div className="pm__form-grid">
                <div className="pm__form-group pm__form-group--full">
                  <label>Product Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    placeholder="e.g., General Liability"
                  />
                </div>

                <div className="pm__form-group pm__form-group--full">
                  <label>Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={2}
                    placeholder="Product description..."
                  />
                </div>

                <div className="pm__form-group pm__form-group--full">
                  <label>Appetite Definition (JSON or text)</label>
                  <textarea
                    value={form.appetite_definition}
                    onChange={(e) => handleFormChange('appetite_definition', e.target.value)}
                    rows={4}
                    placeholder="Define target classes, excluded risks, geographic scope..."
                  />
                </div>

                <div className="pm__form-group pm__form-group--full">
                  <label>Underwriting Guidelines</label>
                  <textarea
                    value={form.underwriting_guidelines}
                    onChange={(e) => handleFormChange('underwriting_guidelines', e.target.value)}
                    rows={4}
                    placeholder="Underwriting guidelines..."
                  />
                </div>

                <div className="pm__form-divider">Pricing Guardrails</div>

                <div className="pm__form-group">
                  <label>Min Premium</label>
                  <input
                    type="number"
                    value={form.min_premium}
                    onChange={(e) => handleFormChange('min_premium', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="pm__form-group">
                  <label>Max Premium</label>
                  <input
                    type="number"
                    value={form.max_premium}
                    onChange={(e) => handleFormChange('max_premium', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="pm__form-group">
                  <label>Min Deductible</label>
                  <input
                    type="number"
                    value={form.min_deductible}
                    onChange={(e) => handleFormChange('min_deductible', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="pm__form-group">
                  <label>Max Deductible</label>
                  <input
                    type="number"
                    value={form.max_deductible}
                    onChange={(e) => handleFormChange('max_deductible', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="pm__form-group">
                  <label>Min Limit</label>
                  <input
                    type="number"
                    value={form.min_limit}
                    onChange={(e) => handleFormChange('min_limit', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div className="pm__form-group">
                  <label>Max Limit</label>
                  <input
                    type="number"
                    value={form.max_limit}
                    onChange={(e) => handleFormChange('max_limit', e.target.value)}
                    placeholder="0"
                  />
                </div>

                <div className="pm__form-group pm__form-group--full">
                  <label>Exception Rules (JSON or text)</label>
                  <textarea
                    value={form.exception_rules}
                    onChange={(e) => handleFormChange('exception_rules', e.target.value)}
                    rows={3}
                    placeholder="Define exceptions to standard pricing and appetite..."
                  />
                </div>

                <div className="pm__form-group">
                  <label>Status</label>
                  <select
                    value={form.active ? 'active' : 'inactive'}
                    onChange={(e) => handleFormChange('active', e.target.value === 'active')}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="pm__form-actions">
                <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Product'}
                </button>
                <button className="btn btn--secondary" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </div>
          ) : prod ? (
            <div className="pm__detail">
              <div className="pm__detail-header">
                <h2 className="pm__detail-name">{prod.Name || '—'}</h2>
                <button className="btn btn--secondary" onClick={handleEdit}>
                  Edit
                </button>
              </div>

              <p className="pm__detail-desc">{prod.Description || 'No description provided.'}</p>

              <div className="pm__detail-section">
                <h4 className="pm__detail-section-title">Appetite Definition</h4>
                {renderObjectOrText(prod.AppetiteDefinition, 'Not yet defined.')}
              </div>

              <div className="pm__detail-section">
                <h4 className="pm__detail-section-title">Pricing Guardrails</h4>
                {renderObjectOrText(prod.PricingGuardrails, 'No pricing guardrails defined.')}
              </div>

              <div className="pm__detail-section">
                <h4 className="pm__detail-section-title">Underwriting Guidelines</h4>
                <p className="pm__detail-text">
                  {prod.UnderwritingGuidelines || 'No underwriting guidelines defined.'}
                </p>
              </div>

              <div className="pm__detail-section">
                <h4 className="pm__detail-section-title">Exception Rules</h4>
                {renderObjectOrText(prod.ExceptionRules, 'No exception rules defined.')}
              </div>
            </div>
          ) : (
            <div className="pm__placeholder">
              <p>Select a product to view details, or add a new one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
